"use client";

// THE COMMUNITY'S SEARCH — the crumb's "/ search", but with no dropdown: what
// you type filters the room you are standing in, live (CommunityClient does
// the filtering; this is only the field). ⌘K focuses it, Escape clears it,
// Enter goes to the best match's room when that is somewhere else.
//
// Two faces of one field: `crumb` sits in the breadcrumb beside "community"
// (the dashboard's .os-crumb-search grammar), and `field` is the soft field a
// phone gets above the chips, because the crumb's search yields under 640px.

import { useEffect, useRef } from "react";

export default function CommunitySearch({
  value,
  onChange,
  onEnter,
  face = "crumb",
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  onEnter?: () => void;
  face?: "crumb" | "field";
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (face !== "crumb") return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ref.current?.focus();
        ref.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [face]);

  const input = (
    <input
      ref={ref}
      type="search"
      enterKeyHint="search"
      autoComplete="off"
      spellCheck={false}
      className={face === "crumb" ? "os-crumb-search cm-search" : "input-soft cm-search cm-search--field"}
      placeholder={face === "crumb" ? "search" : label}
      value={value}
      size={face === "crumb" ? Math.max(6, value.length + 1) : undefined}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Escape") { onChange(""); (e.target as HTMLInputElement).blur(); }
        if (e.key === "Enter") { e.preventDefault(); onEnter?.(); }
        e.stopPropagation();
      }}
      aria-label={label}
    />
  );

  if (face === "field") return input;
  return (
    <span data-noswipe style={{ display: "inline-flex", alignItems: "baseline", gap: "0.5rem", minWidth: 0 }}>
      <span aria-hidden style={{ color: "var(--ink-4)" }}>/</span>
      {input}
    </span>
  );
}
